import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
} from '@mui/material';
import {
  Refresh,
  Send as SendIcon,
  DeleteOutlined,
  AddOutlined,
  PersonRemoveOutlined,
  Visibility,
} from '@mui/icons-material';
import apiService, { ChangelogItem, ChangelogSubscriber } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useThemeMode } from '../contexts/ThemeContext';
import { formatDateTime } from '../utils/datetime';

const formatCountdown = (iso: string | null) => {
  if (!iso) return '—';
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 'due now';
  const hrs = Math.floor(ms / 3_600_000);
  const mins = Math.floor((ms % 3_600_000) / 60_000);
  return hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
};

const statusChip = (status: string) => {
  const map: Record<string, { label: string; color: string; bg: string }> = {
    pending_approval: { label: 'Pending',   color: '#D97706', bg: 'rgba(217,119,6,0.12)' },
    sent:             { label: 'Sent',      color: '#22C55E', bg: 'rgba(34,197,94,0.12)' },
    discarded:        { label: 'Discarded', color: '#888',    bg: 'rgba(136,136,136,0.12)' },
    failed:           { label: 'Failed',    color: '#EF4444', bg: 'rgba(239,68,68,0.12)' },
  };
  const m = map[status] ?? { label: status, color: '#888', bg: 'rgba(136,136,136,0.12)' };
  return (
    <Chip
      label={m.label}
      size="small"
      sx={{ bgcolor: m.bg, color: m.color, fontWeight: 600, fontSize: '0.7rem', height: 22, border: 'none' }}
    />
  );
};

const ChangelogsPage: React.FC = () => {
  const { user } = useAuth();
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';

  const textP = isDark ? '#EFEFEF' : '#0A0A0A';
  const textS = isDark ? '#888888' : '#666666';
  const textM = isDark ? '#555555' : '#AAAAAA';
  const card  = isDark ? '#111111' : '#FFFFFF';
  const border = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)';
  const subBg  = isDark ? 'rgba(255,255,255,0.04)' : '#F8FAFC';

  const isSuperAdmin = user?.role === 'super_admin';

  const [tab, setTab] = useState<'pending' | 'history' | 'subscribers'>('pending');
  const [pending, setPending] = useState<ChangelogItem[]>([]);
  const [history, setHistory] = useState<ChangelogItem[]>([]);
  const [subs, setSubs] = useState<ChangelogSubscriber[]>([]);
  const [autoSendHours, setAutoSendHours] = useState(12);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [preview, setPreview] = useState<ChangelogItem | null>(null);
  const [recipientPreview, setRecipientPreview] = useState<string[] | null>(null);
  const [recipientCount, setRecipientCount] = useState<number | null>(null);
  const [confirmSend, setConfirmSend] = useState<ChangelogItem | null>(null);
  const [addSubOpen, setAddSubOpen] = useState(false);
  const [newSubEmail, setNewSubEmail] = useState('');
  const [snack, setSnack] = useState<{ msg: string; severity: 'success' | 'error' | 'info' } | null>(null);

  const refresh = useCallback(async () => {
    if (!isSuperAdmin) return;
    setLoading(true);
    try {
      const [p, h, s] = await Promise.all([
        apiService.listPendingChangelogs(),
        apiService.listChangelogHistory(),
        apiService.listChangelogSubscribers(),
      ]);
      setPending(p.items);
      setHistory(h.items);
      setSubs(s.items);
      setAutoSendHours(p.auto_send_after_hours);
    } catch (e: any) {
      setSnack({ msg: e?.response?.data?.detail ?? 'Failed to load changelogs', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [isSuperAdmin]);

  useEffect(() => { refresh(); }, [refresh]);

  // Re-render the countdown every minute so "5h 32m" stays accurate without manual refresh.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);
  useMemo(() => tick, [tick]);

  const doSync = async () => {
    setSyncing(true);
    try {
      const result = await apiService.syncChangelogs();
      setSnack({
        msg: `Sync complete — ${result.ingested} new, ${result.skipped} skipped`,
        severity: result.ingested ? 'success' : 'info',
      });
      await refresh();
    } catch (e: any) {
      setSnack({ msg: e?.response?.data?.detail ?? 'Sync failed', severity: 'error' });
    } finally {
      setSyncing(false);
    }
  };

  const doSend = async (item: ChangelogItem) => {
    setBusyId(item.id);
    try {
      const res = await apiService.sendChangelogNow(item.id);
      setSnack({ msg: res.message ?? 'Sent', severity: 'success' });
      await refresh();
    } catch (e: any) {
      setSnack({ msg: e?.response?.data?.detail ?? 'Send failed', severity: 'error' });
    } finally {
      setBusyId(null);
      setConfirmSend(null);
    }
  };

  const doDiscard = async (item: ChangelogItem) => {
    if (!window.confirm(`Discard changelog ${item.version}? It will not be sent. The file remains in the repo but won't be re-ingested.`)) return;
    setBusyId(item.id);
    try {
      await apiService.discardChangelog(item.id);
      setSnack({ msg: 'Discarded', severity: 'info' });
      await refresh();
    } catch (e: any) {
      setSnack({ msg: e?.response?.data?.detail ?? 'Discard failed', severity: 'error' });
    } finally {
      setBusyId(null);
    }
  };

  const openSendDialog = async (item: ChangelogItem) => {
    setConfirmSend(item);
    setRecipientPreview(null);
    setRecipientCount(null);
    try {
      const r = await apiService.getChangelogRecipients();
      setRecipientPreview(r.emails);
      setRecipientCount(r.count);
    } catch {
      // non-fatal — show send button without preview
    }
  };

  const addSubscriber = async () => {
    const email = newSubEmail.trim().toLowerCase();
    if (!email) return;
    try {
      await apiService.addChangelogSubscriber(email);
      setSnack({ msg: `Added ${email}`, severity: 'success' });
      setNewSubEmail('');
      setAddSubOpen(false);
      await refresh();
    } catch (e: any) {
      setSnack({ msg: e?.response?.data?.detail ?? 'Add failed', severity: 'error' });
    }
  };

  const removeSub = async (email: string) => {
    if (!window.confirm(`Remove ${email} from changelog subscribers?`)) return;
    try {
      await apiService.removeChangelogSubscriber(email);
      setSnack({ msg: `Removed ${email}`, severity: 'info' });
      await refresh();
    } catch (e: any) {
      setSnack({ msg: e?.response?.data?.detail ?? 'Remove failed', severity: 'error' });
    }
  };

  if (!isSuperAdmin) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography sx={{ color: textP, fontSize: '1.25rem', fontWeight: 700, mb: 1 }}>Changelogs</Typography>
        <Typography sx={{ color: textS }}>This page is available to super admins only.</Typography>
      </Box>
    );
  }

  const renderPreview = (item: ChangelogItem) => (
    <Box
      sx={{
        '& h2': { fontSize: '1.05rem', fontWeight: 700, color: textP, mt: 2, mb: 1 },
        '& h3': { fontSize: '0.95rem', fontWeight: 600, color: textP, mt: 1.5 },
        '& p':  { color: textS, fontSize: '0.875rem', lineHeight: 1.65 },
        '& ul': { color: textS, fontSize: '0.875rem', pl: 3, lineHeight: 1.8 },
        '& strong': { color: textP },
        '& code': { bgcolor: subBg, px: 0.75, py: 0.25, borderRadius: '4px', fontSize: '0.8rem' },
      }}
      dangerouslySetInnerHTML={{ __html: item.changelog_html }}
    />
  );

  return (
    <Box sx={{ p: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: '1.25rem', color: textP, letterSpacing: '-0.02em' }}>
            Changelogs
          </Typography>
          <Typography sx={{ color: textS, fontSize: '0.875rem', mt: 0.25 }}>
            Release notes pulled from <code style={{ background: 'transparent', color: textM }}>changelogs/</code>. Pending entries auto-send after {autoSendHours}h.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={syncing ? <CircularProgress size={14} /> : <Refresh />} disabled={syncing} onClick={doSync}>
          Sync from repo
        </Button>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ mb: 3, borderBottom: `1px solid ${border}`, '& .MuiTab-root': { textTransform: 'none', minHeight: 40, fontSize: '0.875rem' } }}
      >
        <Tab value="pending" label={`Pending (${pending.length})`} />
        <Tab value="history" label={`History (${history.length})`} />
        <Tab value="subscribers" label={`Subscribers (${subs.length})`} />
      </Tabs>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={28} /></Box>
      ) : tab === 'pending' ? (
        <Paper sx={{ border: `1px solid ${border}`, boxShadow: 'none', borderRadius: '8px', overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {['Version', 'Release date', 'Auto-send in', 'Source', 'Status', ''].map(h => (
                    <TableCell key={h} align={h === '' ? 'right' : 'left'}>
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: textM, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                        {h}
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {pending.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 6, color: textM }}>
                      No changelogs awaiting approval. Commit <code>changelogs/&lt;version&gt;.md</code> and redeploy, or click <strong>Sync from repo</strong>.
                    </TableCell>
                  </TableRow>
                ) : pending.map(item => (
                  <TableRow key={item.id} sx={{ '&:hover': { bgcolor: subBg } }}>
                    <TableCell>
                      <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: textP }}>{item.version}</Typography>
                    </TableCell>
                    <TableCell sx={{ color: textS, fontSize: '0.875rem' }}>{item.release_date}</TableCell>
                    <TableCell sx={{ color: textS, fontSize: '0.875rem' }}>{formatCountdown(item.auto_send_after)}</TableCell>
                    <TableCell sx={{ color: textM, fontSize: '0.8rem', fontFamily: 'JetBrains Mono, monospace' }}>{item.source_file ?? '—'}</TableCell>
                    <TableCell>{statusChip(item.status)}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Preview"><IconButton size="small" onClick={() => setPreview(item)} sx={{ color: textM, '&:hover': { color: textP } }}><Visibility fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Send now"><IconButton size="small" disabled={busyId === item.id} onClick={() => openSendDialog(item)} sx={{ color: textM, '&:hover': { color: '#22C55E' } }}><SendIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Discard"><IconButton size="small" disabled={busyId === item.id} onClick={() => doDiscard(item)} sx={{ color: textM, '&:hover': { color: '#EF4444' } }}><DeleteOutlined fontSize="small" /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ) : tab === 'history' ? (
        <Paper sx={{ border: `1px solid ${border}`, boxShadow: 'none', borderRadius: '8px', overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {['Version', 'Release date', 'Status', 'Sent at', 'Sent by', 'Recipients', ''].map(h => (
                    <TableCell key={h} align={h === '' ? 'right' : 'left'}>
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: textM, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                        {h}
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {history.length === 0 ? (
                  <TableRow><TableCell colSpan={7} align="center" sx={{ py: 6, color: textM }}>No history yet.</TableCell></TableRow>
                ) : history.map(item => (
                  <TableRow key={item.id} sx={{ '&:hover': { bgcolor: subBg } }}>
                    <TableCell><Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: textP }}>{item.version}</Typography></TableCell>
                    <TableCell sx={{ color: textS, fontSize: '0.875rem' }}>{item.release_date}</TableCell>
                    <TableCell>{statusChip(item.status)}</TableCell>
                    <TableCell sx={{ color: textS, fontSize: '0.875rem' }}>{item.sent_at ? formatDateTime(item.sent_at, user?.timezone) : '—'}</TableCell>
                    <TableCell sx={{ color: textS, fontSize: '0.875rem' }}>{item.sent_by ?? '—'}</TableCell>
                    <TableCell sx={{ color: textS, fontSize: '0.875rem' }}>{item.recipient_count ?? '—'}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Preview"><IconButton size="small" onClick={() => setPreview(item)} sx={{ color: textM, '&:hover': { color: textP } }}><Visibility fontSize="small" /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ) : (
        <Box>
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography sx={{ color: textS, fontSize: '0.875rem' }}>
              Extra recipients on top of the platform default (all admins, viewers, app_viewers).
            </Typography>
            <Button variant="outlined" startIcon={<AddOutlined />} onClick={() => setAddSubOpen(true)}>
              Add subscriber
            </Button>
          </Box>
          <Paper sx={{ border: `1px solid ${border}`, boxShadow: 'none', borderRadius: '8px', overflow: 'hidden' }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {['Email', 'Added by', 'Added at', ''].map(h => (
                      <TableCell key={h} align={h === '' ? 'right' : 'left'}>
                        <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: textM, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                          {h}
                        </Typography>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {subs.length === 0 ? (
                    <TableRow><TableCell colSpan={4} align="center" sx={{ py: 6, color: textM }}>No extra subscribers.</TableCell></TableRow>
                  ) : subs.map(s => (
                    <TableRow key={s.email} sx={{ '&:hover': { bgcolor: subBg } }}>
                      <TableCell sx={{ color: textP, fontSize: '0.875rem' }}>{s.email}</TableCell>
                      <TableCell sx={{ color: textS, fontSize: '0.875rem' }}>{s.added_by}</TableCell>
                      <TableCell sx={{ color: textS, fontSize: '0.875rem' }}>{s.added_at ? formatDateTime(s.added_at, user?.timezone) : '—'}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Remove"><IconButton size="small" onClick={() => removeSub(s.email)} sx={{ color: textM, '&:hover': { color: '#EF4444' } }}><PersonRemoveOutlined fontSize="small" /></IconButton></Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      )}

      {/* Preview dialog */}
      <Dialog open={!!preview} onClose={() => setPreview(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: card, color: textP }}>
          {preview?.version} <Typography component="span" sx={{ color: textM, fontSize: '0.875rem', ml: 2 }}>{preview?.release_date}</Typography>
        </DialogTitle>
        <DialogContent sx={{ bgcolor: card }}>
          {preview && renderPreview(preview)}
        </DialogContent>
        <DialogActions sx={{ bgcolor: card }}>
          <Button onClick={() => setPreview(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Send confirmation dialog */}
      <Dialog open={!!confirmSend} onClose={() => setConfirmSend(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: card, color: textP }}>Send {confirmSend?.version}?</DialogTitle>
        <DialogContent sx={{ bgcolor: card }}>
          <Typography sx={{ color: textS, fontSize: '0.875rem', mb: 2 }}>
            This will email the rendered changelog immediately and mark it as sent. The same version cannot be sent again.
          </Typography>
          {recipientCount === null ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: textM }}>
              <CircularProgress size={14} /> Loading recipients…
            </Box>
          ) : (
            <Box>
              <Typography sx={{ color: textP, fontSize: '0.875rem', fontWeight: 600, mb: 1 }}>
                Recipients ({recipientCount})
              </Typography>
              <Box sx={{ maxHeight: 200, overflow: 'auto', bgcolor: subBg, p: 1.5, borderRadius: '6px', fontFamily: 'JetBrains Mono, monospace', fontSize: '0.78rem', color: textS }}>
                {recipientPreview?.join(', ') ?? '—'}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ bgcolor: card }}>
          <Button onClick={() => setConfirmSend(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!confirmSend || busyId === confirmSend.id || recipientCount === 0}
            onClick={() => confirmSend && doSend(confirmSend)}
            startIcon={busyId === confirmSend?.id ? <CircularProgress size={14} /> : <SendIcon />}
          >
            Send now
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add subscriber dialog */}
      <Dialog open={addSubOpen} onClose={() => setAddSubOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ bgcolor: card, color: textP }}>Add changelog subscriber</DialogTitle>
        <DialogContent sx={{ bgcolor: card }}>
          <TextField
            autoFocus
            fullWidth
            label="Email"
            type="email"
            value={newSubEmail}
            onChange={e => setNewSubEmail(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') addSubscriber(); }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ bgcolor: card }}>
          <Button onClick={() => setAddSubOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!newSubEmail.includes('@')} onClick={addSubscriber}>Add</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snack} autoHideDuration={4000} onClose={() => setSnack(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        {snack ? <Alert onClose={() => setSnack(null)} severity={snack.severity} sx={{ width: '100%' }}>{snack.msg}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
};

export default ChangelogsPage;
